import React, { useState, useEffect, useMemo } from 'react';
import { Shield, MapPin, Clock, AlertTriangle, CheckCircle, X, RefreshCcw, Trash2, Flag, User, Phone, ChevronDown, ChevronUp, Loader2, DollarSign, Calendar, Filter } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';
import { formatDistanceToNow, format, startOfMonth, endOfMonth, subMonths, isWithinInterval, parseISO } from 'date-fns';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  available: 'bg-green-100 text-green-800',
  bidding: 'bg-indigo-100 text-indigo-800',
  allocated: 'bg-blue-100 text-blue-800',
  on_the_way: 'bg-amber-100 text-amber-800',
  arrived: 'bg-purple-100 text-purple-800',
  collected: 'bg-teal-100 text-teal-800',
  swarm_departed: 'bg-stone-100 text-stone-600',
  cancelled: 'bg-red-100 text-red-600',
  unable_to_collect: 'bg-orange-100 text-orange-700',
};

const SITUATION_LABELS = {
  swarm_tree_branch: 'Tree/Branch',
  swarm_fence_post: 'Fence/Post',
  swarm_ground_level: 'Ground Level',
  swarm_elevated: 'Elevated',
  swarm_building_exterior: 'Building Exterior',
  bees_in_wall: 'Wall Cavity',
  bees_in_roof: 'Roof Space',
  bees_in_chimney: 'Chimney',
  bees_in_ground: 'Ground/Retaining',
  established_colony: 'Established Colony',
  other: 'Other',
};

const BID_STATUS_COLORS = {
  pending: 'text-amber-700 bg-amber-50',
  accepted: 'text-green-700 bg-green-50 font-bold',
  rejected: 'text-stone-400 bg-stone-50 line-through',
};

export default function AdminSwarmDashboard() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [expandedId, setExpandedId] = useState(null);
  const [dateFilter, setDateFilter] = useState('all');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchReports = async () => {
    try {
      const res = await axios.get(`${API}/api/swarm-reports/admin/all`, { headers });
      setReports(res.data || []);
    } catch {
      toast.error('Failed to load swarm reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, []);

  const handleAction = async (reportId, action) => {
    try {
      await axios.put(`${API}/api/swarm-reports/admin/${reportId}/reassign`, { action }, { headers });
      toast.success(`Report ${action}led`);
      fetchReports();
    } catch {
      toast.error('Action failed');
    }
  };

  // Date filter logic
  const dateFiltered = useMemo(() => {
    if (dateFilter === 'all') return reports;
    const now = new Date();
    let start, end;
    if (dateFilter === 'this_month') {
      start = startOfMonth(now);
      end = endOfMonth(now);
    } else if (dateFilter === 'last_month') {
      const last = subMonths(now, 1);
      start = startOfMonth(last);
      end = endOfMonth(last);
    } else if (dateFilter === '3_months') {
      start = startOfMonth(subMonths(now, 2));
      end = endOfMonth(now);
    } else {
      return reports;
    }
    return reports.filter(r => {
      try {
        const d = parseISO(r.created_at);
        return isWithinInterval(d, { start, end });
      } catch { return true; }
    });
  }, [reports, dateFilter]);

  const filtered = filter === 'all' ? dateFiltered : dateFiltered.filter(r => r.status === filter);
  const statusCounts = dateFiltered.reduce((acc, r) => { acc[r.status] = (acc[r.status] || 0) + 1; return acc; }, {});

  if (loading) return <div className="p-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-stone-800 flex items-center gap-2">
          <Shield className="h-6 w-6 text-amber-600" />
          Swarm Reports Admin
        </h2>
        <div className="flex items-center gap-2">
          <select
            value={dateFilter}
            onChange={e => setDateFilter(e.target.value)}
            className="text-sm border border-stone-300 rounded-lg px-3 py-1.5 bg-white"
            data-testid="date-filter"
          >
            <option value="all">All Time</option>
            <option value="this_month">This Month</option>
            <option value="last_month">Last Month</option>
            <option value="3_months">Last 3 Months</option>
          </select>
          <Button onClick={fetchReports} variant="outline" size="sm">
            <RefreshCcw className="h-4 w-4 mr-1" /> Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <button onClick={() => setFilter('all')} className={`p-3 rounded-xl border text-center ${filter === 'all' ? 'border-amber-400 bg-amber-50' : 'border-stone-200'}`}>
          <div className="text-2xl font-bold text-stone-800">{dateFiltered.length}</div>
          <div className="text-xs text-stone-500">Total</div>
        </button>
        {['available', 'bidding', 'allocated', 'collected'].map(s => (
          <button key={s} onClick={() => setFilter(s)} className={`p-3 rounded-xl border text-center ${filter === s ? 'border-amber-400 bg-amber-50' : 'border-stone-200'}`}>
            <div className="text-2xl font-bold" style={{ color: s === 'available' ? '#15803d' : s === 'bidding' ? '#4338ca' : s === 'allocated' ? '#1d4ed8' : '#0d9488' }}>{statusCounts[s] || 0}</div>
            <div className="text-xs text-stone-500 capitalize">{s}</div>
          </button>
        ))}
      </div>

      {/* Reports List */}
      <div className="space-y-2">
        {filtered.length === 0 && <p className="text-center py-8 text-stone-500">No reports matching filter</p>}
        {filtered.map(r => {
          const expanded = expandedId === r.id;
          const bids = r.bids || [];
          const hasBids = bids.length > 0;
          const acceptedBid = bids.find(b => b.status === 'accepted');
          return (
            <Card key={r.id} className={`border ${r.duplicate_flag ? 'border-orange-300 bg-orange-50' : 'border-stone-200'}`}>
              <CardContent className="p-3">
                <div
                  className="flex items-center gap-2 flex-wrap cursor-pointer"
                  onClick={() => setExpandedId(expanded ? null : r.id)}
                >
                  <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_COLORS[r.status] || 'bg-stone-100'}`}>
                    {r.status?.replace(/_/g, ' ').toUpperCase()}
                  </span>
                  <span className="font-medium text-stone-800 text-sm">{SITUATION_LABELS[r.situation_type] || r.situation_type}</span>
                  {r.is_paid_removal && <span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-blue-50 text-blue-700 border border-blue-200">Paid</span>}
                  <span className="text-xs text-stone-500 flex items-center gap-1"><MapPin className="h-3 w-3" />{r.suburb || r.address?.slice(0, 30)}</span>
                  <span className="text-xs text-stone-400">{r.created_at ? formatDistanceToNow(new Date(r.created_at), { addSuffix: true }) : ''}</span>
                  {hasBids && <span className="text-xs bg-indigo-100 text-indigo-700 px-1.5 py-0.5 rounded-full flex items-center gap-1"><DollarSign className="h-3 w-3" />{bids.length} bid{bids.length > 1 ? 's' : ''}</span>}
                  {r.accepted_bid_amount && <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded-full font-bold">${r.accepted_bid_amount}</span>}
                  {r.duplicate_flag && <span className="text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full flex items-center gap-1"><Flag className="h-3 w-3" />Dup</span>}
                  {r.already_arranged && <span className="text-xs bg-yellow-200 text-yellow-800 px-2 py-0.5 rounded-full">Arranged</span>}
                  <span className="text-[10px] font-mono text-stone-400 bg-stone-100 px-1.5 py-0.5 rounded">{r.ref || `SW-${r.id?.slice(0, 6).toUpperCase()}`}</span>
                  <div className="ml-auto text-stone-400">
                    {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                  </div>
                </div>

                {expanded && (
                  <div className="mt-3 pt-3 border-t border-stone-100 space-y-2 text-sm">
                    <div className="grid grid-cols-2 gap-2">
                      <div><strong>Reporter:</strong> {r.reporter_name}</div>
                      <div><strong>Phone:</strong> <a href={`tel:${r.reporter_phone}`} className="text-blue-600 underline">{r.reporter_phone}</a></div>
                      <div><strong>Email:</strong> {r.reporter_email || '—'}</div>
                      <div><strong>Height:</strong> {r.height_estimate || '—'}</div>
                      <div className="col-span-2"><strong>Address:</strong> {r.address}</div>
                      <div className="col-span-2"><strong>Access:</strong> {r.access_notes || '—'}</div>
                      {r.description && <div className="col-span-2"><strong>Description:</strong> {r.description}</div>}
                    </div>

                    {r.assigned_to_name && (
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-2">
                        <strong>Assigned to:</strong> {r.assigned_to_name} (ETA: {r.estimated_arrival || '—'})
                        {r.assigned_at && <span className="text-xs ml-2 text-stone-500">at {format(new Date(r.assigned_at), 'PPp')}</span>}
                      </div>
                    )}

                    {/* Bid History */}
                    {hasBids && (
                      <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                        <h4 className="text-xs font-bold text-indigo-800 mb-2 flex items-center gap-1"><DollarSign className="h-3.5 w-3.5" />Quotes ({bids.length})</h4>
                        <div className="space-y-1.5">
                          {bids.sort((a, b) => a.amount - b.amount).map((b, idx) => (
                            <div key={b.id || idx} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-md ${BID_STATUS_COLORS[b.status] || 'bg-stone-50'}`}>
                              <span className="font-bold text-base">${b.amount?.toFixed(0)}</span>
                              <span className="font-medium">{b.user_name}</span>
                              {b.includes_repair && <span className="text-[10px] bg-blue-100 text-blue-700 px-1 py-0.5 rounded">+repair</span>}
                              {b.estimated_hours > 0 && <span className="text-stone-500">~{b.estimated_hours}h</span>}
                              <span className={`ml-auto px-1.5 py-0.5 rounded text-[10px] font-bold uppercase ${b.status === 'accepted' ? 'bg-green-200 text-green-800' : b.status === 'rejected' ? 'bg-red-100 text-red-600' : 'bg-amber-100 text-amber-700'}`}>
                                {b.status || 'pending'}
                              </span>
                              {b.description && <span className="text-stone-500 truncate max-w-[200px]" title={b.description}>{b.description}</span>}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {r.standby_list?.length > 0 && <div className="text-xs text-stone-500">{r.standby_list.length} on standby</div>}
                    {r.duplicate_flag && (
                      <div className="bg-orange-100 border border-orange-300 rounded-lg p-2 text-orange-800 text-xs">
                        Possible duplicate of report {r.duplicate_flag.possible_duplicate_of?.slice(0, 8)} ({r.duplicate_flag.distance_m}m apart)
                      </div>
                    )}
                    {/* Status History */}
                    {r.status_history?.length > 0 && (
                      <details className="text-xs">
                        <summary className="cursor-pointer text-stone-500 font-medium">Status History ({r.status_history.length})</summary>
                        <div className="mt-1 space-y-1">
                          {r.status_history.map((h, i) => (
                            <div key={i} className="flex gap-2">
                              <span className="text-stone-400">{h.at ? format(new Date(h.at), 'PP p') : ''}</span>
                              <span className="font-medium">{h.status}</span>
                              {h.reason && <span className="text-stone-500">— {h.reason}</span>}
                            </div>
                          ))}
                        </div>
                      </details>
                    )}
                    {/* Admin Actions */}
                    <div className="flex gap-2 pt-2">
                      {r.status !== 'available' && r.status !== 'collected' && r.status !== 'cancelled' && (
                        <Button onClick={() => handleAction(r.id, 'reset')} variant="outline" size="sm" className="text-amber-700 border-amber-300">
                          <RefreshCcw className="h-3 w-3 mr-1" /> Reset to Available
                        </Button>
                      )}
                      {r.status !== 'cancelled' && (
                        <Button onClick={() => handleAction(r.id, 'cancel')} variant="outline" size="sm" className="text-red-600 border-red-300">
                          <Trash2 className="h-3 w-3 mr-1" /> Cancel
                        </Button>
                      )}
                    </div>
                    <div className="text-xs text-stone-400">ID: {r.id}</div>
                  </div>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>
    </div>
  );
}
